import {
  STORE_KEY_ACCOUNT,
  STORE_KEY_ACCOUNT_DATA_RETENTION,
  STORE_KEY_ACCOUNT_OPTIONS,
  STORE_KEY_AUTH,
  STORE_KEY_FEATURES,
  STORE_KEY_ID_LIST,
  STORE_KEY_LANG,
  STORE_KEY_LIST,
  STORE_KEY_LISTS_COUNT,
  STORE_KEY_LOGIN_TYPE,
  STORE_KEY_SECKEY,
  STORE_KEY_USER,
  STORE_KEY_USER_LANG,
  STORE_KEY_USER_LISTS,
  STORE_KEY_USER_OPTIONS,
  gainsightIdentify,
  googleTagMangerInit,
  sessionStoreSet,
  storeSet,
  gainsightSetglobalContext,
} from "mailup-react-helpers";

declare global {
  interface Window {
    aptrinsic?: (...args: any[]) => void;
  }
}

import {
  beAccountSettingsApiGetSessionOauth2Token,
  beAccountSettingsApiGetUser,
} from "Api/beAccountSettingsApi";
import {
  beAccountsApiGetAccountConfiguration,
  beAccountsApiGetAccountStatus,
  beAccountsApiGetOptions,
  beAccountsGetAccountDataRetention,
} from "Api/beAccountsApi";
import { beFeatureToggleGetFlagsToShow } from "Api/beFeaturetoggle";
import { BeListApiGetList, beListGetUserVisibleLists } from "Api/beListApi";

import { beListApiGetListOfNlList } from "Api/beListApiTS";

import {
  APP_CLIENT_ID,
  LOGIN_TYPE_ADM,
  LOGIN_TYPE_ADMLIMITED,
  LOGIN_TYPE_RESELLER,
  offerType,
} from "Constants";
import type { Option } from "Types/beAccountsApi";
import type { Feature } from "Types/beFeatureToggleApi";

import PendoTracking from "Helpers/pendoTracking";

const initOptions: string[] = [
  "DELSTATS",
  "EDIT",
  "GEST",
  "NL",
  "NOEXPORT",
  "NOIMPORT",
  "PRIVACYBLOCK",
  "SMS",
  "STAT",
  "WHITELABEL",
  "TURNOFFMSHOP",
  "ADVRECIPIENTMGMT",
  "ENGAGEMENT",
  "ADVSEGMENT",
  "FORCEANAGFLDGEST",
  "USER",
  "GROUP",
];
const initFeatures: string[] = ["NewImportWizardFile"];

const formatOption = (optionId: number): string => {
  return `${optionId}`.padStart(3, "0");
};

const getSHA256Hash = async (input: string) => {
  const textAsBuffer = new TextEncoder().encode(input);
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", textAsBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hash = hashArray
    .map((item) => item.toString(16).padStart(2, "0"))
    .join("");
  return hash;
};

const getOauthToken = async (seckey: string): Promise<void> => {
  const { token } = await beAccountSettingsApiGetSessionOauth2Token({
    seckey,
    client_id: APP_CLIENT_ID,
  });

  sessionStoreSet(STORE_KEY_AUTH, token);
  sessionStoreSet(STORE_KEY_SECKEY, seckey);
};

export const initApp = async ({
  seckey,
  idList,
  loginType,
}: {
  seckey: string;
  idList: number;
  loginType: number;
}): Promise<{
  lang: string;
  userLang: string;
}> => {
  await getOauthToken(seckey);

  const promises = [
    beAccountsApiGetAccountConfiguration(),
    beAccountsApiGetAccountStatus(),
    beAccountSettingsApiGetUser(0),
    BeListApiGetList(idList),
    beListApiGetListOfNlList({
      force: false,
      pageSize: 0,
      pageNumber: 0,
      orderBy: "",
      orderDescending: false,
    }),
    beAccountsGetAccountDataRetention(),
    initFeatures.length > 0
      ? beFeatureToggleGetFlagsToShow(initFeatures)
      : { Features: [] },
    beAccountsApiGetOptions(initOptions),
  ];

  const [
    resAccount,
    resAccountStatus,
    user,
    list,
    listOfNlList,
    accountDataRetention,
    resFeatures,
    resOptions,
  ] = await Promise.all(promises);

  if (list === null) {
    throw new Error("list is null");
  }
  if (user === null) {
    throw new Error("user is null");
  }

  const account = {
    Id: resAccount.Id,
    Domain: resAccount.Domain,
    Language: resAccount.Language,
    Options: resAccount.Options,
    Version: resAccount.Version,
    DataRetention: resAccount.DataRetention,
    SendMethodType: resAccount.SendMethodType,
    Bandwidth: resAccount.Bandwidth,
    IsSendMethodTypeFrequency: resAccount.SendMethodType === 1,
    Vdir: resAccount.Dir,
    IdOffer: resAccount.IdOffer,
    IsTrial: resAccount.IsTrial,
    IsRetailer: resAccount.IsRetailer,
    IsDeveloper: resAccount.IsDeveloper,
    GracePeriod: resAccountStatus.GracePeriod,
    GracePeriodNote: resAccountStatus.GracePeriodNote,
    SuspendType: resAccountStatus.SuspendType,
    SuspendDate: resAccountStatus.SuspendDate,
    IsCustomer: resAccount.IsCustomer,
    Environment: resAccount.Environment,
    Suspended: resAccount.Suspended,
    IsOfferLight: resAccount.IdOffer === offerType.LightEdition,
  };

  const lang = resAccount.Language.toLowerCase();

  storeSet(STORE_KEY_ACCOUNT, account);
  storeSet(STORE_KEY_USER, user);
  storeSet(STORE_KEY_LIST, list);
  storeSet(STORE_KEY_ID_LIST, list.IdList);
  storeSet(STORE_KEY_LISTS_COUNT, listOfNlList.TotalItems);
  storeSet(
    STORE_KEY_ACCOUNT_DATA_RETENTION,
    accountDataRetention > 365 ? 365 : accountDataRetention,
  );

  const userListsResponse = await beListGetUserVisibleLists(user.Id);

  storeSet(STORE_KEY_USER_LISTS, userListsResponse.Items);

  storeSet(STORE_KEY_LANG, lang);
  storeSet(STORE_KEY_LOGIN_TYPE, loginType);

  const userLang =
    typeof user?.Lang === "string" && user?.Lang.length > 0
      ? user?.Lang.toLowerCase()
      : lang;

  storeSet(STORE_KEY_USER_LANG, userLang);

  const numericUserOptions = user.Options.split(",");
  const numericAccountOptions = account.Options.split(",");

  const userOptions: string[] = resOptions.Items.filter((o: Option) =>
    numericUserOptions.includes(formatOption(o.Id)),
  ).map((o: Option) => o.ShortName);

  const accountOptions: string[] = resOptions.Items.filter((o: Option) =>
    numericAccountOptions.includes(formatOption(o.Id)),
  ).map((o: Option) => o.ShortName);

  const features = resFeatures.Features.filter((o: Feature) => o.Show).map(
    (o: Feature) => o.Name,
  );

  storeSet(STORE_KEY_USER_OPTIONS, userOptions);
  storeSet(STORE_KEY_ACCOUNT_OPTIONS, accountOptions);
  storeSet(STORE_KEY_FEATURES, features);

  PendoTracking(
    //  visitor
    {
      id: user.Id,
    },
    //  account
    {
      id: account.Id,
      isLoginFromAdm: [LOGIN_TYPE_ADM, LOGIN_TYPE_ADMLIMITED].includes(
        loginType,
      ),
      isLoginReseller: loginType === LOGIN_TYPE_RESELLER,
    },
  );

  const userIdHash = await getSHA256Hash(user.Id.toString());
  const accountIdHash = await getSHA256Hash(account.Id.toString());

  await googleTagMangerInit(resAccount.Environment);

  await gainsightIdentify(
    {
      id: userIdHash,
    },
    {
      id: accountIdHash,
    },
  );

  await gainsightSetglobalContext({ commercialOfferName: "mup" });

  return {
    lang,
    userLang,
  };
};
